import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Plus, FileText } from "lucide-react";
import api from "@/lib/api";

const MONTHS_FR = ["Janvier", "Fevrier", "Mars", "Avril", "Mai", "Juin", "Juillet", "Aout", "Septembre", "Octobre", "Novembre", "Decembre"];
const DAYS_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function EditorialCalendar() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/calendar?month=${month}&year=${year}`)
      .then((res) => setArticles(res.data.articles || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [month, year]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const daysInMonth = lastDay.getDate();
  let startDay = firstDay.getDay() - 1; // Monday=0
  if (startDay < 0) startDay = 6;

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const getArticlesForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return articles.filter(a => {
      const pubDate = (a.publication_date || a.created_at || "").substring(0, 10);
      return pubDate === dateStr;
    });
  };

  const statusColors = {
    published: "bg-emerald-500",
    draft: "bg-amber-500",
    scheduled: "bg-blue-500",
  };

  const isToday = (day) => {
    return day === now.getDate() && month === now.getMonth() + 1 && year === now.getFullYear();
  };

  return (
    <div data-testid="editorial-calendar">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl">Calendrier éditorial</h1>
          <p className="text-muted-foreground mt-1">Planifiez et visualisez vos publications</p>
        </div>
        <Link to="/admin/articles/new">
          <Button className="rounded-none" data-testid="calendar-new-article">
            <Plus className="h-4 w-4 mr-2" /> Nouvel article
          </Button>
        </Link>
      </div>

      {/* Month Navigation */}
      <Card className="border rounded-none mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="prev-month">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h2 className="font-heading text-xl" data-testid="current-month">
              {MONTHS_FR[month - 1]} {year}
            </h2>
            <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="next-month">
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex gap-4 mb-4">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Publié
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <div className="h-2.5 w-2.5 rounded-full bg-amber-500" /> Brouillon
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <div className="h-2.5 w-2.5 rounded-full bg-blue-500" /> Programmé
        </div>
      </div>

      {/* Calendar Grid */}
      <Card className="border rounded-none">
        <CardContent className="p-0">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b">
            {DAYS_FR.map((d) => (
              <div key={d} className="p-2 text-center text-xs font-medium text-muted-foreground border-r last:border-r-0">
                {d}
              </div>
            ))}
          </div>
          {/* Calendar cells */}
          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {cells.map((day, i) => {
              const dayArticles = getArticlesForDay(day);
              return (
                <div
                  key={i}
                  className={`min-h-[100px] border-r border-b last:border-r-0 p-1.5 ${!day ? "bg-muted/30" : ""} ${isToday(day) ? "bg-primary/5" : ""}`}
                  data-testid={day ? `calendar-day-${day}` : undefined}
                >
                  {day && (
                    <>
                      <div className={`text-xs mb-1 ${isToday(day) ? "font-bold text-primary" : "text-muted-foreground"}`}>
                        {day}
                      </div>
                      <div className="space-y-0.5">
                        {dayArticles.slice(0, 3).map((a) => (
                          <Link
                            key={a.id}
                            to={`/admin/articles/${a.id}/edit`}
                            className="block group"
                          >
                            <div className="flex items-start gap-1 p-1 rounded-sm hover:bg-accent transition-colors">
                              <div className={`h-1.5 w-1.5 rounded-full mt-1 shrink-0 ${statusColors[a.status] || "bg-muted-foreground"}`} />
                              <span className="text-[10px] leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                                {a.title}
                              </span>
                            </div>
                          </Link>
                        ))}
                        {dayArticles.length > 3 && (
                          <p className="text-[10px] text-muted-foreground px-1">+{dayArticles.length - 3} autre{dayArticles.length - 3 > 1 ? "s" : ""}</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Articles this month list */}
      <Card className="border rounded-none mt-6">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Articles de {MONTHS_FR[month - 1].toLowerCase()} ({articles.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground py-4">Chargement...</p>
          ) : articles.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Aucun article ce mois-ci</p>
          ) : (
            <div className="space-y-2" data-testid="calendar-article-list">
              {articles.map((a) => (
                <Link
                  key={a.id}
                  to={`/admin/articles/${a.id}/edit`}
                  className="flex items-center justify-between py-2 px-2 -mx-2 rounded-sm hover:bg-accent transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`h-2 w-2 rounded-full shrink-0 ${statusColors[a.status] || "bg-muted-foreground"}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{a.title}</p>
                      <p className="text-xs text-muted-foreground">{a.author?.name || "—"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <Badge variant="outline" className="rounded-none text-[10px]">
                      {a.status === "published" ? "Publié" : a.status === "draft" ? "Brouillon" : "Programmé"}
                    </Badge>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {(a.publication_date || a.created_at) ? new Date(a.publication_date || a.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "—"}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
